import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";

export type PageToolbarTab = {
    value: string;
    label: string;
    count?: number;
};

type Props = {
    title: string;
    /** Optional count badge next to the title. */
    count?: number;
    /** Right-aligned action buttons (or any controls). */
    actions?: ReactNode;
    /** Optional filter / status tabs under the header. */
    tabs?: PageToolbarTab[];
    tabValue?: string;
    onTabChange?: (value: string) => void;
};

/**
 * Shared list-page chrome: heading + optional count, actions, and counted tabs.
 * Domain-agnostic — pass title/actions/tabs from the host view.
 */
export function PageToolbar({
    title,
    count,
    actions,
    tabs,
    tabValue,
    onTabChange,
}: Props) {
    return (
        <>
            <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 px-4 py-4">
                <div className="flex items-center gap-3">
                    {/* Avoid native h1; AlbertineTypography.css sizes h1–h5 and
                        overrides Tailwind heading utilities in production LMT. */}
                    <p
                        className="heading-3 text-zinc-900"
                        role="heading"
                        aria-level={1}
                    >
                        {title}
                    </p>
                    {count !== undefined ? (
                        <Badge variant="secondary">{count}</Badge>
                    ) : null}
                </div>
                <div className="flex-1" />
                {actions}
            </div>

            {tabs && tabs.length > 0 && tabValue && onTabChange ? (
                <Tabs value={tabValue} onValueChange={onTabChange} className="shrink-0">
                    <TabsList className="px-4">
                        {tabs.map((tab) => (
                            <TabsTrigger key={tab.value} value={tab.value}>
                                {tab.label}
                                {tab.count !== undefined ? (
                                    <span className="ml-1.5 text-xs text-zinc-400 tabular-nums leading-4">
                                        {tab.count}
                                    </span>
                                ) : null}
                            </TabsTrigger>
                        ))}
                        <TabsIndicator />
                    </TabsList>
                </Tabs>
            ) : null}
        </>
    );
}
